import {isWeaveObjectRef, ObjectRef, parseRefMaybe} from '@wandb/weave/react';
import _ from 'lodash';
import {useEffect, useMemo, useState} from 'react';

import {flattenObjectPreservingWeaveTypes} from '../../../../flattenObject';
import {TraceServerClient} from '../../../wfReactInterface/traceServerClient';
import {useGetTraceServerClientContext} from '../../../wfReactInterface/traceServerClientContext';
import {TraceTableQueryReq} from '../../../wfReactInterface/traceServerClientTypes';
import {projectIdFromParts} from '../../../wfReactInterface/tsDataModelHooks';
import {useCompareEvaluationsState} from '../../compareEvaluationsContext';
import {
  buildCompositeMetricsMap,
  CompositeScoreMetrics,
  resolvePeerDimension,
} from '../../compositeMetricsUtil';
import {
  EvaluationComparisonState,
  getOrderedCallIds,
  ScoreFilter,
} from '../../ecpState';
import {PredictAndScoreCall} from '../../ecpTypes';
import {
  metricDefinitionId,
  resolveScoreMetricValueForPASCall,
} from '../../ecpUtil';
import {InputCachingManager} from './useInputCachingManager';

type RowBase = {
  id: string;
  evaluationCallId: string;
  inputDigest: string;
  inputRef: string;
  path: string[];
  predictAndScore: PredictAndScoreCall;
};

type FlattenedRow = RowBase & {
  output: {[outputKey: string]: any};
  predictCallId: string | undefined;
  scores: {[scoreId: string]: number | boolean | undefined};
  trialNdx: number;
};

export type PivotedRow = RowBase & {
  output: {[outputKey: string]: {[callId: string]: any}};
  scores: {[scoreId: string]: {[callId: string]: number | boolean | undefined}};
  predictCallId: string | undefined;
};

const aggregateGroupedNestedRows = <T>(
  rows: PivotedRow[],
  field: keyof PivotedRow,
  aggFunc: (vals: any[]) => T
) => {
  return Object.fromEntries(
    Object.entries(
      rows.reduce<{
        [flatKey: string]: {[callId: string]: any[]};
      }>((acc, row) => {
        Object.entries(row[field] ?? {}).forEach(([key, val]) => {
          Object.entries(val).forEach(([subKey, subVal]) => {
            if (acc[key] == null) {
              acc[key] = {};
            }
            if (acc[key][subKey] == null) {
              acc[key][subKey] = [];
            }
            acc[key][subKey].push(subVal);
          });
        });
        return acc;
      }, {})
    ).map(([key, val]) => {
      return [
        key,
        Object.fromEntries(
          Object.entries(val).map(([subKey, subVal]) => {
            return [subKey, aggFunc(subVal)];
          })
        ),
      ];
    })
  );
};

const filterNones = (list: any[]) => {
  return list.filter(v => v != null);
};

const rowIsSelected = (
  scores: {
    [dimensionId: string]: {
      [evaluationCallId: string]: number | undefined;
    };
  },
  state: EvaluationComparisonState,
  compositeMetricsMap: CompositeScoreMetrics
) => {
  const compareDims = state.comparisonDimensions;

  if (compareDims == null || compareDims.length === 0) {
    return true;
  }
  return compareDims.every(compareDim => {
    if (
      compareDim.rangeSelection == null ||
      Object.entries(compareDim.rangeSelection).length === 0
    ) {
      return true;
    }
    return Object.entries(compareDim.rangeSelection).every(
      ([evalCallId, range]) => {
        const resolvedPeerDim = resolvePeerDimension(
          compositeMetricsMap,
          evalCallId,
          state.summary.scoreMetrics[compareDim.metricId]
        );
        if (resolvedPeerDim == null) {
          return false;
        }
        const values = scores[metricDefinitionId(resolvedPeerDim)];
        if (values[evalCallId] == null) {
          return false;
        }
        const rowVal = values[evalCallId] as number;
        return range.min <= rowVal && rowVal <= range.max;
      }
    );
  });
};

export type FilteredAggregateRows = {
  id: string;
  count: number;
  inputDigest: string;
  inputRef: ObjectRef | null;
  output: {
    [k: string]: {
      [k: string]: any;
    };
  };
  scores: {
    [k: string]: {
      [k: string]: number | undefined;
    };
  };
  originalRows: PivotedRow[];
  predictCallIds: Record<string, string | undefined>;
}[];

const applyScoreFilters = <T extends {scores: any}>(
  rows: T[],
  scoreFilters: ScoreFilter[],
  evaluationCallId: string,
  compositeMetrics: CompositeScoreMetrics
): T[] => {
  const completeFilters = scoreFilters.filter(
    f => f.scoreKey && f.scoreKey !== ''
  );

  if (completeFilters.length === 0) {
    return rows;
  }

  return rows.filter(row => {
    return completeFilters.every(filter => {
      let metricDef;
      for (const group of Object.values(compositeMetrics)) {
        if (group.metrics[filter.scoreKey]) {
          metricDef = Object.values(
            group.metrics[filter.scoreKey].scorerRefs
          )[0]?.metric;
          break;
        }
      }

      if (!metricDef) {
        return false;
      }

      const resolvedMetric = resolvePeerDimension(
        compositeMetrics,
        evaluationCallId,
        metricDef
      );

      if (!resolvedMetric) {
        return false;
      }

      const scoreId = metricDefinitionId(resolvedMetric);
      const scoreValue = row.scores?.[scoreId]?.[evaluationCallId];

      if (scoreValue == null) {
        return false;
      }

      const numericValue =
        typeof scoreValue === 'number' ? scoreValue : Number(scoreValue);
      if (isNaN(numericValue)) {
        return false;
      }

      switch (filter.operator) {
        case '(number): =':
          return numericValue === filter.value;
        case '(number): !=':
          return numericValue !== filter.value;
        case '(number): <':
          return numericValue < filter.value;
        case '(number): <=':
          return numericValue <= filter.value;
        case '(number): >':
          return numericValue > filter.value;
        case '(number): >=':
          return numericValue >= filter.value;
        default:
          return true;
      }
    });
  });
};

export const useFilteredAggregateRows = (
  state: EvaluationComparisonState
): {
  filteredRows: FilteredAggregateRows;
  outputColumnKeys: string[];
  leafDims: string[];
} => {
  const leafDims = useMemo(() => getOrderedCallIds(state), [state]);
  const compositeMetricsMap = useMemo(
    () => buildCompositeMetricsMap(state.summary, 'score'),
    [state.summary]
  );

  const flattenedRows = useMemo(() => {
    const rows: FlattenedRow[] = [];
    Object.entries(
      state.loadableComparisonResults.result?.resultRows ?? {}
    ).forEach(([rowDigest, rowCollection]) => {
      Object.values(rowCollection.evaluations).forEach(modelCollection => {
        Object.values(modelCollection.predictAndScores).forEach(
          (predictAndScoreRes, trialNdx) => {
            const output = predictAndScoreRes._rawPredictTraceData?.output;
            rows.push({
              id: predictAndScoreRes.callId,
              evaluationCallId: predictAndScoreRes.evaluationCallId,
              inputDigest: predictAndScoreRes.rowDigest,
              inputRef: predictAndScoreRes.exampleRef,
              // Note: this would be a possible location to record the raw predict_and_score inputs as the presumed data row.
              output: flattenObjectPreservingWeaveTypes({output}),
              predictCallId: predictAndScoreRes._rawPredictTraceData?.id,
              scores: Object.fromEntries(
                [...Object.entries(state.summary.scoreMetrics)].map(
                  ([scoreKey, scoreVal]) => {
                    return [
                      scoreKey,
                      resolveScoreMetricValueForPASCall(
                        scoreVal,
                        predictAndScoreRes
                      ),
                    ];
                  }
                )
              ),
              path: [
                rowDigest,
                predictAndScoreRes.evaluationCallId,
                predictAndScoreRes.callId,
              ],
              predictAndScore: predictAndScoreRes,
              trialNdx,
            });
          }
        );
      });
    });
    return rows;
  }, [
    state.loadableComparisonResults.result?.resultRows,
    state.summary.scoreMetrics,
  ]);

  const pivotedRows = useMemo(() => {
    // Ok, so in this step we are going to pivot -
    // id: string; - no change
    // inputDigest: string; - no change
    // input: {[inputKey: string]: any}; - no change
    // evaluationCallId: string; - Each key will be divided into new leafs
    // output: {[outputKey: string]: any}; - Each key will be divided into new leafs
    // scores: {[scoreId: string]: number | boolean}; - Each key will be divided into new leafs
    // latency: number; - Each key will be divided into new leafs
    // totalTokens: number; - Each key will be divided into new leafs
    // path: string[]; - no change
    const expandPrimitive = (obj: any, evaluationCallId: string) => {
      return Object.fromEntries(
        leafDims.map(d => {
          return [d, evaluationCallId === d ? obj : null];
        })
      );
    };

    const expandDict = (obj: any, evaluationCallId: string) => {
      return Object.fromEntries(
        Object.entries(obj).map(([key, val]) => {
          return [key, expandPrimitive(val, evaluationCallId)];
        })
      );
    };

    return flattenedRows.map(row => {
      return {
        ...row,
        output: expandDict(row.output, row.evaluationCallId),
        scores: expandDict(row.scores, row.evaluationCallId),
      };
    }) as PivotedRow[];
  }, [flattenedRows, leafDims]);

  const aggregatedRows = useMemo(() => {
    const grouped = _.groupBy(pivotedRows, row => row.inputDigest);
    return Object.fromEntries(
      Object.entries(grouped).map(([inputDigest, rows]) => {
        return [
          inputDigest,
          {
            id: inputDigest, // required for the data grid
            count: rows.length,
            inputDigest,
            inputRef: parseRefMaybe(rows[0].inputRef), // Should be the same for all,
            output: aggregateGroupedNestedRows(
              rows,
              'output',
              vals => filterNones(vals)[0]
            ),
            predictCallIds: rows.reduce(
              (acc, {evaluationCallId, predictCallId}) => {
                acc[evaluationCallId] = predictCallId as string | undefined;
                return acc;
              },
              {} as Record<string, string | undefined>
            ),
            scores: aggregateGroupedNestedRows(rows, 'scores', vals => {
              const allVals = filterNones(vals);
              if (allVals.length === 0) {
                return undefined;
              }
              return _.mean(
                allVals.map(v => {
                  if (typeof v === 'number') {
                    return v;
                  } else if (typeof v === 'boolean') {
                    return v ? 1 : 0;
                  } else {
                    return 0;
                  }
                })
              );
            }),
            originalRows: rows,
          },
        ];
      })
    );
  }, [pivotedRows]);

  const filteredRows = useMemo(() => {
    const aggregatedAsList = Object.values(aggregatedRows);
    const compareDims = state.comparisonDimensions;
    let res = aggregatedAsList;
    if (compareDims != null && compareDims.length > 0) {
      const allowedDigests = Object.keys(aggregatedRows).filter(digest => {
        return rowIsSelected(
          aggregatedRows[digest].scores,
          state,
          compositeMetricsMap
        );
      });

      res = aggregatedAsList.filter(row =>
        allowedDigests.includes(row.inputDigest)
      );
    }
    if (compareDims != null && compareDims.length > 0) {
      // Sort by the difference between the max and min values
      const compareDim = compareDims[0];
      res = _.sortBy(res, row => {
        const values =
          aggregatedRows[row.inputDigest].scores[compareDim.metricId];
        const valuesAsNumbers = Object.values(values).map(v => {
          if (typeof v === 'number') {
            return v;
          } else if (typeof v === 'boolean') {
            return v ? 1 : 0;
          } else {
            return 0;
          }
        });
        return -(Math.max(...valuesAsNumbers) - Math.min(...valuesAsNumbers));
      });
    }

    // Apply score filters if we have a single evaluation
    const onlyOneModel = state.evaluationCallIdsOrdered.length === 1;
    if (onlyOneModel && state.scoreFilters && state.scoreFilters.length > 0) {
      const evaluationCallId = state.evaluationCallIdsOrdered[0];
      res = applyScoreFilters(
        res,
        state.scoreFilters,
        evaluationCallId,
        compositeMetricsMap
      );
    }

    return res;
  }, [aggregatedRows, compositeMetricsMap, state]);

  const outputColumnKeys = useMemo(() => {
    const keys = new Set<string>();
    const keysList: string[] = [];
    flattenedRows.forEach(row => {
      Object.keys(row.output).forEach(key => {
        if (!keys.has(key)) {
          keys.add(key);
          keysList.push(key);
        }
      });
    });
    return keysList;
  }, [flattenedRows]);

  return useMemo(() => {
    return {
      filteredRows,
      outputColumnKeys,
      leafDims,
    };
  }, [filteredRows, leafDims, outputColumnKeys]);
};

/**
 * The following functions are used to fetch data rows using the InputCachingManager:
 * * `useExampleCompareDataAndPrefetch` - Fetches a target row and prefetches adjacent rows.
 *   * This is the primary exported hook from this section.
 * * `useExampleCompareData` - Fetches a single row using the cache manager.
 * * `usePartialTableRequest` - Prepares the table request parameters for the cache manager.
 */

// React hook to fetch the target row and prefetch adjacent rows
// This is a convenience hook for the `ExampleCompareSection` component
export function useExampleCompareDataAndPrefetch(
  inputCacheManager: InputCachingManager,
  filteredRows: Array<{
    inputDigest: string;
  }>,
  targetIndex: number
) {
  const ctx = useCompareEvaluationsState();
  const resultRows = ctx.state.loadableComparisonResults.result?.resultRows;

  const targetDigest = filteredRows[targetIndex].inputDigest;
  const [loading, setLoading] = useState<boolean>(true);
  const [targetRowValue, setTargetRowValue] = useState<any>(null);

  // Compute all digests to request (target + adjacent)
  const digestsToRequest = useMemo(() => {
    const digests = [targetDigest];
    if (targetIndex > 0) {
      digests.push(filteredRows[targetIndex - 1].inputDigest);
    }
    if (targetIndex < filteredRows.length - 1) {
      digests.push(filteredRows[targetIndex + 1].inputDigest);
    }
    return digests;
  }, [filteredRows, targetIndex, targetDigest]);

  useEffect(() => {
    // If data is already available, use it immediately
    const cachedData = resultRows?.[targetDigest]?.rawDataRow;
    if (cachedData != null) {
      setLoading(false);
      setTargetRowValue(flattenObjectPreservingWeaveTypes(cachedData));
      return;
    }

    // Otherwise, use cache manager
    const unsubscribe = inputCacheManager.eventObserver.subscribe(evt => {
      if (evt.digest === targetDigest) {
        setLoading(evt.status === 'loading');
        if (evt.data != null) {
          setTargetRowValue(flattenObjectPreservingWeaveTypes(evt.data));
        }
      }
    });

    // Request all digests at once (cache manager batches them)
    digestsToRequest.forEach(digest => {
      inputCacheManager.requestRowData(digest);
    });

    return () => unsubscribe();
  }, [targetDigest, inputCacheManager, digestsToRequest, resultRows]);

  return {
    targetRowValue,
    loading,
  };
}

// Primary method for fetching and caching a single row using the cache manager
export function useExampleCompareData(
  inputCacheManager: InputCachingManager,
  targetDigest: string
) {
  const ctx = useCompareEvaluationsState();
  const resultRows = ctx.state.loadableComparisonResults.result?.resultRows;

  const [loading, setLoading] = useState<boolean>(true);
  const [targetRowValue, setTargetRowValue] = useState<any>(null);

  useEffect(() => {
    // If data is already available, use it immediately
    const cachedData = resultRows?.[targetDigest]?.rawDataRow;
    if (cachedData != null) {
      setLoading(false);
      setTargetRowValue(flattenObjectPreservingWeaveTypes(cachedData));
      return;
    }
    // Otherwise, use cache manager
    inputCacheManager.eventObserver.subscribe(evt => {
      if (evt.digest === targetDigest) {
        setLoading(evt.status === 'loading');
        if (evt.data != null) {
          setTargetRowValue(flattenObjectPreservingWeaveTypes(evt.data));
        }
      }
    });

    inputCacheManager.requestRowData(targetDigest);

    // Note: We intentionally do NOT unsubscribe in cleanup.
    // This prevents premature cache eviction when components scroll out of view.
    // The cache is only purged on pagination changes.
    // Keeping subscriptions alive is lightweight and maintains cache efficiency.
  }, [targetDigest, inputCacheManager, resultRows]);

  return {
    targetRowValue,
    loading,
  };
}

type PartialTableRequestType = Pick<
  TraceTableQueryReq,
  'project_id' | 'digest'
>;

// Get the table digest used in the dataset of the first evaluation,
// which prepares a request for actually fetching the table rows later
async function makePartialTableReq(
  client: TraceServerClient,
  datasetRef: string
): Promise<PartialTableRequestType | null> {
  const datasetObjRes = await client.readBatch({
    refs: [datasetRef],
  });
  if (!datasetObjRes.vals[0]) {
    console.error('Dataset not found');
    return null;
  }

  const rowsRef = datasetObjRes.vals[0].rows;
  const parsedRef = parseRefMaybe(rowsRef);
  if (parsedRef == null) {
    console.error('Invalid rows ref', rowsRef);
    return null;
  }
  if (!isWeaveObjectRef(parsedRef)) {
    console.error('Ref is not a weave object ref', rowsRef);
    return null;
  }
  if (parsedRef.weaveKind !== 'table') {
    console.error('Ref is not a table ref', rowsRef);
    return null;
  }

  return {
    project_id: projectIdFromParts({
      entity: parsedRef.entityName,
      project: parsedRef.projectName,
    }),
    digest: parsedRef.artifactVersion,
  };
}

// React hook to wrap the `makePartialTableReq`
export const usePartialTableRequest = (state: EvaluationComparisonState) => {
  const getTraceServerClient = useGetTraceServerClientContext();
  const client = getTraceServerClient();
  const [partialTableRequest, setPartialTableRequest] =
    useState<PartialTableRequestType | null>(null);

  const datasetRef = useMemo(() => {
    return Object.values(state.summary.evaluations)[0].datasetRef as string;
  }, [state.summary.evaluations]);

  useEffect(() => {
    let mounted = true;
    makePartialTableReq(client, datasetRef).then(res => {
      if (mounted) {
        setPartialTableRequest(res);
      }
    });
    return () => {
      mounted = false;
    };
  }, [client, datasetRef]);

  return partialTableRequest;
};

export const removePrefix = (key: string, prefix: string) => {
  if (key.startsWith(prefix)) {
    return key.slice(prefix.length);
  }
  return key;
};
